import { useEffect, useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import { Box, Chip, Loader, Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import { mfeRegistry, type MfeDefinition } from "@repo/mfe-registry";

interface VersionInfo {
  sha: string;
  branch: string;
  buildTime: string;
}

function formatBuildTime(iso: string): string {
  try {
    return new Date(iso).toUTCString();
  } catch {
    return iso;
  }
}

async function fetchVersionJson(url: string): Promise<VersionInfo> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<VersionInfo>;
}

function getMfeUseProd(mfeName: string): boolean {
  const globalFlag = import.meta.env.VITE_MFE_USE_PROD === "true";
  const key = `VITE_MFE_USE_PROD_${mfeName.toUpperCase().replace(/-/g, "_")}`;
  const override = (import.meta.env as Record<string, string | undefined>)[key];
  return override !== undefined ? override === "true" : globalFlag;
}

function versionUrlFromMfe(mfe: MfeDefinition): string | null {
  const useProd = !import.meta.env.DEV || getMfeUseProd(mfe.name);

  if (useProd) {
    const prodUrl = mfe.moduleFederation.prodUrl;
    if (!prodUrl) return null;
    return prodUrl.replace("/remoteEntry.js", "/version.json");
  }

  return mfe.moduleFederation.devUrl.replace(
    "/remoteEntry.js",
    "/version.json",
  );
}

interface RowProps {
  name: string;
  info?: VersionInfo;
  isLoading: boolean;
  isError: boolean;
}

function VersionRow({ name, info, isLoading, isError }: RowProps) {
  return (
    <tr style={{ borderBottom: "1px solid var(--color-border-subtle)" }}>
      <td className="py-3 pr-6">
        <Typography kind="body2">{name}</Typography>
      </td>
      <td className="py-3 pr-6">
        {isLoading ? (
          <Loader kind="loop" format="inline" size="small" color="brand" />
        ) : isError || !info ? (
          <Chip label="Unavailable" color="neutral" />
        ) : (
          <Typography kind="body2">{info.sha}</Typography>
        )}
      </td>
      <td className="py-3 pr-6">
        {!isLoading && !isError && info ? (
          <Typography kind="body2">{info.branch}</Typography>
        ) : null}
      </td>
      <td className="py-3">
        {!isLoading && !isError && info ? (
          <Typography kind="body2">
            {formatBuildTime(info.buildTime)}
          </Typography>
        ) : null}
      </td>
    </tr>
  );
}

export default function VersionPage() {
  const timeRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const update = () => {
      if (timeRef.current)
        timeRef.current.textContent = new Date().toUTCString();
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  const tenantId = localStorage.getItem("tenantId");

  const mfeQueries = useQueries({
    queries: mfeRegistry.map((mfe) => {
      const versionUrl = versionUrlFromMfe(mfe);
      return {
        queryKey: ["version", mfe.name],
        queryFn: () => fetchVersionJson(versionUrl!),
        enabled: versionUrl !== null,
        retry: false,
        staleTime: Infinity,
      };
    }),
  });

  const hostInfo: VersionInfo = {
    sha: __BUILD_SHA__,
    branch: __BUILD_BRANCH__,
    buildTime: __BUILD_TIME__,
  };

  return (
    <Box
      className="min-h-screen p-8"
      style={{ backgroundColor: "var(--color-background-primary)" }}
    >
      <Typography kind="h1" className="mb-2">
        <FormattedMessage
          id="JfQeFL"
          defaultMessage="Version Info"
          description="Version page title"
        />
      </Typography>
      <div className="mb-8" style={{ color: "var(--color-text-secondary)" }}>
        <Typography kind="body2">
          <FormattedMessage
            id="z5D0/G"
            defaultMessage="Build and deployment details for this application and its microfrontends."
            description="Version page description"
          />
        </Typography>
      </div>

      <div
        className="rounded-lg mb-6 p-4"
        style={{
          border: "1px solid var(--color-border-subtle)",
          backgroundColor: "var(--color-background-secondary-subtlest)",
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Typography kind="label">
              <FormattedMessage
                id="HTcrWx"
                defaultMessage="Current Date / Time (UTC)"
                description="Version page current datetime label"
              />
            </Typography>
            <Typography kind="body2">
              <span ref={timeRef} />
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Typography kind="label">
              <FormattedMessage
                id="31qz8k"
                defaultMessage="Tenant ID"
                description="Version page tenant id label"
              />
            </Typography>
            {tenantId ? (
              <Typography kind="body2">{tenantId}</Typography>
            ) : (
              <Chip label="Unavailable" color="neutral" />
            )}
          </div>
        </div>
      </div>

      <div
        className="rounded-lg overflow-hidden"
        style={{
          border: "1px solid var(--color-border-subtle)",
          backgroundColor: "var(--color-background-secondary-subtlest)",
        }}
      >
        <table className="w-full">
          <thead>
            <tr
              style={{ borderBottom: "2px solid var(--color-border-subtle)" }}
            >
              <th className="py-3 pr-6 text-left">
                <Typography kind="label">
                  <FormattedMessage
                    id="Tyhyw+"
                    defaultMessage="Service"
                    description="Version table service column header"
                  />
                </Typography>
              </th>
              <th className="py-3 pr-6 text-left">
                <Typography kind="label">
                  <FormattedMessage
                    id="OEafUV"
                    defaultMessage="Git Hash"
                    description="Version table git hash column header"
                  />
                </Typography>
              </th>
              <th className="py-3 pr-6 text-left">
                <Typography kind="label">
                  <FormattedMessage
                    id="xH9XhK"
                    defaultMessage="Branch"
                    description="Version table branch column header"
                  />
                </Typography>
              </th>
              <th className="py-3 text-left">
                <Typography kind="label">
                  <FormattedMessage
                    id="azKNsN"
                    defaultMessage="Build Time"
                    description="Version table build time column header"
                  />
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={{ borderBottom: "1px solid var(--color-border-subtle)" }}
            >
              <td className="py-3 pr-6">
                <Typography kind="body2">
                  <FormattedMessage
                    id="AGFaqI"
                    defaultMessage="ispm-host"
                    description="Version table host service name"
                  />
                </Typography>
              </td>
              <td className="py-3 pr-6">
                <Typography kind="body2">{hostInfo.sha}</Typography>
              </td>
              <td className="py-3 pr-6">
                <Typography kind="body2">{hostInfo.branch}</Typography>
              </td>
              <td className="py-3">
                <Typography kind="body2">
                  {formatBuildTime(hostInfo.buildTime)}
                </Typography>
              </td>
            </tr>
            {mfeRegistry.map((mfe, i) => (
              <VersionRow
                key={mfe.name}
                name={mfe.name}
                info={mfeQueries[i].data}
                isLoading={mfeQueries[i].isLoading}
                isError={mfeQueries[i].isError}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Box>
  );
}
